import { useEffect, useMemo, useState } from "react";
import { Card, Table, Select, Space, Typography, Button, Tag, Statistic, Row, Col } from "antd";
import { FileExcelOutlined, ReloadOutlined, ClockCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../variables/numbersFunctions";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";

const { Title, Text } = Typography;

// « Émission reportée » : émissions cédées à effet d'exercice FUTUR, non encore réparties lors de la
// rétrocession — elles attendent leur exercice d'effet (et ses clés de répartition) pour l'être.
const PrimesReportees = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [annee, setAnnee] = useState(null);
    const [branche, setBranche] = useState(null);

    const charger = () => {
        setLoading(true);
        const params = {};
        if (annee) params.annee = annee;
        if (branche) params.branche = branche;
        axiosInstance
            .get("/api/retrocession/primes-reportees", { params })
            .then((res) => setRows(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des émissions reportées");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        axiosInstance.get("/api/branche").then((res) => setBranches(res.data || [])).catch(() => setBranches([]));
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { charger(); }, [annee, branche]);

    const total = useMemo(() => rows.reduce((s, r) => s + (Number(r?.montant) || 0), 0), [rows]);
    const nbCompagnies = useMemo(() => new Set(rows.map((r) => r?.compagnieCode).filter(Boolean)).size, [rows]);

    const frenchDate = (d) => {
        if (!d || typeof d !== "string") return "—";
        const s = d.slice(0, 10);
        const p = s.split("-");
        return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s;
    };

    const columns = [
        { title: "Compagnie cédante", dataIndex: "compagnie", key: "compagnie",
          sorter: (a, b) => String(a.compagnie || "").localeCompare(String(b.compagnie || "")) },
        { title: "Branche", dataIndex: "branche", key: "branche", width: 90,
          render: (v) => v ? <Tag color="blue">{v}</Tag> : "—" },
        { title: "Mois de cession", dataIndex: "moisCession", key: "moisCession", width: 130,
          sorter: (a, b) => String(a.moisCession || "").localeCompare(String(b.moisCession || "")) },
        { title: "N° Police", dataIndex: "numeroPolice", key: "numeroPolice", render: (v) => v || "—" },
        { title: "Assuré", dataIndex: "assure", key: "assure", render: (v) => v || "—" },
        { title: "Date d'effet", dataIndex: "effet", key: "effet", width: 120, render: (v) => frenchDate(v) },
        { title: "Exercice d'effet", dataIndex: "exerciceEffet", key: "exerciceEffet", width: 130, align: "center",
          render: (v) => v ? <Tag color="gold">{v}</Tag> : "—",
          sorter: (a, b) => (a.exerciceEffet || 0) - (b.exerciceEffet || 0) },
        { title: "Émission nette reportée", dataIndex: "montant", key: "montant", align: "right", width: 170,
          render: (v) => <Text strong>{displayNumbers(Math.round(Number(v) || 0))} F</Text>,
          sorter: (a, b) => (a.montant || 0) - (b.montant || 0) },
    ];

    const exporter = () => {
        if (!rows.length) { toast.warning("Rien à exporter"); return; }
        const data = rows.map((r) => ({
            "Compagnie cédante": r.compagnie,
            "Branche": r.branche,
            "Mois de cession": r.moisCession,
            "N° Police": r.numeroPolice,
            "Assuré": r.assure,
            "Date d'effet": frenchDate(r.effet),
            "Exercice d'effet": r.exerciceEffet,
            "Émission nette reportée": Math.round(Number(r.montant) || 0),
        }));
        data.push({ "Compagnie cédante": "TOTAL", "Émission nette reportée": Math.round(total) });
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Émissions reportées");
        XLSX.writeFile(wb, `Emissions_reportees${annee ? "_" + annee : ""}${branche ? "_" + branche : ""}.xlsx`);
    };

    const anneesOptions = useMemo(() => {
        const ys = new Set(rows.map((r) => r?.exerciceEffet).filter(Boolean));
        const now = new Date().getFullYear();
        for (let y = now - 1; y <= now + 3; y++) ys.add(y);
        return [...ys].sort((a, b) => b - a).map((y) => ({ label: String(y), value: y }));
    }, [rows]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"OPERATIONS"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">
        <div style={{ padding: 16 }}>
            <Title level={4} style={{ color: "#242166", marginBottom: 4 }}>
                <ClockCircleOutlined /> Émissions reportées (en attente de répartition)
            </Title>
            <Text type="secondary">
                Émissions cédées à effet d'exercice futur, non encore réparties — elles seront rétrocédées à leur
                exercice d'effet, avec les clés de répartition correspondantes.
            </Text>

            <Row gutter={16} style={{ margin: "16px 0" }}>
                <Col><Statistic title="Total émission reportée" value={Math.round(total)} suffix="F" formatter={(v) => displayNumbers(v)} /></Col>
                <Col><Statistic title="Lignes" value={rows.length} /></Col>
                <Col><Statistic title="Compagnies" value={nbCompagnies} /></Col>
            </Row>

            <Card size="small" style={{ marginBottom: 12 }}>
                <Space wrap>
                    <Select
                        allowClear placeholder="Exercice d'effet" style={{ width: 170 }}
                        value={annee} onChange={setAnnee} options={anneesOptions}
                    />
                    <Select
                        allowClear placeholder="Branche" style={{ width: 170 }}
                        value={branche} onChange={setBranche}
                        options={(branches || []).map((b) => ({ label: b.codeBranche, value: b.codeBranche }))}
                    />
                    <Button icon={<ReloadOutlined />} onClick={charger}>Actualiser</Button>
                    <Button type="primary" ghost icon={<FileExcelOutlined />} onClick={exporter}>Exporter (Excel)</Button>
                </Space>
            </Card>

            <Table
                rowKey={(r, i) => `${r.compagnieCode}-${r.numeroPolice}-${r.moisCession}-${i}`}
                columns={columns}
                dataSource={rows}
                loading={loading}
                size="small"
                bordered
                pagination={{ pageSize: 20, showSizeChanger: true }}
                summary={(data) => {
                    const t = data.reduce((s, r) => s + (Number(r?.montant) || 0), 0);
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={7}><Text strong>TOTAL</Text></Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align="right">
                                    <Text strong>{displayNumbers(Math.round(t))} F</Text>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default PrimesReportees;
